import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Plus, Copy, LogOut as LeaveIcon, Trash2, Crown
} from "lucide-react";
import { API, useAuth, authFetch } from "@/App";

export default function Groups() {
  const navigate = useNavigate();
  useAuth();
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [newName, setNewName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await authFetch(`${API}/groups`);
      if (res.ok) setGroups(await res.json());
    } catch (err) { console.error("groups load failed:", err); }
    setIsLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createGroup = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Group name required");
    setBusy(true);
    try {
      const res = await authFetch(`${API}/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name })
      });
      if (res.ok) {
        toast.success("Group created");
        setShowCreate(false);
        setNewName("");
        await load();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Create failed");
      }
    } finally { setBusy(false); }
  };

  const joinGroup = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return toast.error("Invite code required");
    setBusy(true);
    try {
      const res = await authFetch(`${API}/groups/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invite_code: code })
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(d.message || "Joined");
        setShowJoin(false);
        setJoinCode("");
        await load();
        if (d.group_id) navigate(`/groups/${d.group_id}`);
      } else {
        toast.error(d.detail || "Join failed");
      }
    } finally { setBusy(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success(`Code ${code} copied`),
      () => toast.error("Copy failed")
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center">
        <div className="spinner w-12 h-12" />
      </div>
    );
  }

  return (
    <div data-testid="groups-page" className="min-h-screen bg-islamic-blue-950">
      <header className="glass border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-slate-400 hover:text-gold-400 transition-colors font-outfit"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gold-400" />
            <h1 className="font-cinzel text-lg font-semibold text-slate-100">Family & Groups</h1>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-3 mb-8">
          <Button
            data-testid="create-group-btn"
            onClick={() => setShowCreate(true)}
            className="btn-gold rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" /> New Group
          </Button>
          <Button
            data-testid="join-group-btn"
            variant="ghost"
            onClick={() => setShowJoin(true)}
            className="rounded-full border border-white/10 text-slate-200 hover:bg-white/5"
          >
            Join with Code
          </Button>
        </div>

        {groups.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-gold-400/10 flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-gold-400/50" />
            </div>
            <h2 className="font-cinzel text-xl text-slate-100 mb-2">No Groups Yet</h2>
            <p className="text-slate-500 font-outfit">
              Create a group for your family or class, or join one with a code.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {groups.map((g) => (
              <button
                key={g.group_id}
                data-testid={`group-card-${g.group_id}`}
                onClick={() => navigate(`/groups/${g.group_id}`)}
                className="glass-card rounded-xl p-5 text-left card-lift"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-cinzel text-lg text-slate-100">{g.name}</h3>
                  {g.owner_id === g.members?.[0] /* purely cosmetic */ && (
                    <Crown className="w-4 h-4 text-gold-400/70" />
                  )}
                </div>
                <p className="text-slate-400 text-sm font-outfit">
                  {g.members?.length || 1} member{(g.members?.length || 1) !== 1 ? "s" : ""}
                </p>
                <div
                  className="mt-3 flex items-center gap-2 text-xs font-outfit text-slate-500"
                  onClick={(e) => { e.stopPropagation(); copyCode(g.invite_code); }}
                >
                  <span>Invite:</span>
                  <code className="px-2 py-0.5 bg-islamic-blue-800/60 rounded text-gold-400 tracking-wider">
                    {g.invite_code}
                  </code>
                  <Copy className="w-3 h-3" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-islamic-blue-900 border-white/10 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-slate-100">Create Group</DialogTitle>
            <DialogDescription className="text-slate-400 font-outfit text-sm">
              Give your family or class circle a name. You'll get an invite code to share.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="group-name-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Our Family Hifz Circle"
            maxLength={60}
            className="bg-islamic-blue-950/60 border-white/10 text-slate-200"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-slate-400">
              Cancel
            </Button>
            <Button
              data-testid="confirm-create-group-btn"
              onClick={createGroup}
              disabled={busy}
              className="btn-gold"
            >
              {busy ? "Creating…" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showJoin} onOpenChange={setShowJoin}>
        <DialogContent className="bg-islamic-blue-900 border-white/10 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-slate-100">Join Group</DialogTitle>
            <DialogDescription className="text-slate-400 font-outfit text-sm">
              Enter the 6-character invite code shared with you.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="invite-code-input"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            maxLength={6}
            className="bg-islamic-blue-950/60 border-white/10 text-slate-200 tracking-widest text-center font-mono"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setShowJoin(false)} className="text-slate-400">
              Cancel
            </Button>
            <Button
              data-testid="confirm-join-group-btn"
              onClick={joinGroup}
              disabled={busy}
              className="btn-gold"
            >
              {busy ? "Joining…" : "Join"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
